import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from 'helpers/axios_base';
import { LoginResponse, ResponseT, Admin } from './interfaces';
import { links } from 'helpers/urls';
import storage from 'redux-persist/lib/storage';
// import { useNavigate } from 'react-router-dom';
import { ErrorResponseEnum } from 'enums';

interface AuthState {
	admin?: Admin | null;
	expiration?: Date | null;
	token?: string | null;
	twoFactorRequired?: boolean;
	loading: boolean;
	error: string | null;
	isLogged: boolean;
	logoutTimerId?: number | null;
	inactivityTimerId?: number | null;
	notifications?: any[] | null;
}

const initialState: AuthState = {
	admin: null,
	expiration: null,
	token: null,
	twoFactorRequired: false,
	loading: false,
	error: null,
	isLogged: false,
	logoutTimerId: null,
	inactivityTimerId: null,
	notifications: null,
};

// Async thunk for login
export const loginStaff = createAsyncThunk(
	links.login,
	async (credentials: { email: string; password: string; code?: string }, thunkAPI) => {
		try {
			const response = await axiosInstance.patch<ResponseT<LoginResponse>>(
				links.login,
				credentials,
			);
			const { admin, access_token, expires_at, unread_notifications } = response.data.data;

			if (admin.tfa_enabled) {
				return {
					twoFactorRequired: true,
					admin,
				};
			}

			return {
				twoFactorRequired: false,
				admin,
				expiration: expires_at,
				token: access_token,
				notifications: unread_notifications,
			};
		} catch (error: any) {
			return thunkAPI.rejectWithValue(error.response?.data || error.message);
		}
	},
);

// Async thunk for 2FA verification
export const verifyTwoFactor = createAsyncThunk(
	links.loginVerify,
	async (data: { token: string; id: number }, thunkAPI) => {
		try {
			const response = await axiosInstance.post<ResponseT<LoginResponse>>(
				links.loginVerify,
				data,
			);
			const { admin, access_token, expires_at, unread_notifications } = response.data.data;
			return {
				admin,
				token: access_token,
				expiration: expires_at,
				notifications: unread_notifications,
			};
		} catch (error: any) {
			return thunkAPI.rejectWithValue(error.response?.data || error.message);
		}
	},
);

// Async thunk for fetching the updated staff
export const fetchStaff = createAsyncThunk(links.accountProfile, async (_, thunkAPI) => {
	try {
		const state = thunkAPI.getState() as { auth: AuthState };
		const token = state.auth.token;

		if (!token) {
			throw new Error(ErrorResponseEnum.TOKEN_NOT_FOUND);
		}

		const response = await axiosInstance.get<ResponseT<Admin>>(links.accountProfile, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		return response.data.data;
	} catch (error: any) {
		return thunkAPI.rejectWithValue(error.response?.data || error.message);
	}
});

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		logout: (state) => {
			if (state.logoutTimerId) clearTimeout(state.logoutTimerId);
			if (state.inactivityTimerId) clearTimeout(state.inactivityTimerId);
			state.admin = null;
			state.token = null;
			state.expiration = null;
			state.twoFactorRequired = false;
			state.isLogged = false;
			state.error = null;
			state.loading = false;
			state.notifications = null;

			storage.removeItem('persist:root');
		},
		setLogoutTimer: (state, action: PayloadAction<{ expiration: Date }>) => {
			if (state.logoutTimerId) {
				clearTimeout(state.logoutTimerId);
				state.logoutTimerId = null;
			}

			const expirationTime = new Date(action.payload.expiration).getTime() - Date.now();

			if (expirationTime > 0) {
				state.logoutTimerId = window.setTimeout(() => {
					state.admin = null;
					state.token = null;
					state.expiration = null;
					state.twoFactorRequired = false;
					state.isLogged = false;
					state.logoutTimerId = null;
					state.loading = false;
					state.notifications = null;

					storage.removeItem('persist:root');

					alert(ErrorResponseEnum.SESSION_EXPIRED);
				}, expirationTime);
			}
		},
		setInactivityTimer: (state, action: PayloadAction<number>) => {
			if (state.inactivityTimerId) clearTimeout(state.inactivityTimerId);

			state.inactivityTimerId = window.setTimeout(() => {
				state.admin = null;
				state.token = null;
				state.expiration = null;
				state.twoFactorRequired = false;
				state.isLogged = false;
				state.inactivityTimerId = null;
				state.loading = false;
				state.notifications = null;

				storage.removeItem('persist:root');
				alert(ErrorResponseEnum.LOGGED_OUT_DUE_TO_INACTIVITY);
			}, action.payload);
		},
		resetInactivityTimer: (state, action: PayloadAction<number>) => {
			if (state.inactivityTimerId) {
				clearTimeout(state.inactivityTimerId);
				state.inactivityTimerId = null;
			}

			state.inactivityTimerId = window.setTimeout(() => {
				state.admin = null;
				state.token = null;
				state.expiration = null;
				state.twoFactorRequired = false;
				state.isLogged = false;
				state.inactivityTimerId = null;
				state.loading = false;
				state.notifications = null;

				storage.removeItem('persist:root');
				alert(ErrorResponseEnum.LOGGED_OUT_DUE_TO_INACTIVITY);
			}, action.payload);
		},
	},
	extraReducers: (builder) => {
		builder
			// Login staff
			.addCase(loginStaff.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				loginStaff.fulfilled,
				(
					state,
					action: PayloadAction<
						| {
								twoFactorRequired: true;
								admin: Admin;
						  }
						| {
								twoFactorRequired: false;
								admin: Admin;
								token: string;
								expiration: string;
								notifications?: any[] | null;
						  }
						| any
					>,
				) => {
					state.loading = false;

					if (action.payload.twoFactorRequired) {
						// Handle the 2FA required case
						state.admin = action.payload.admin;
						state.twoFactorRequired = true;
					} else {
						// Handle the regular login case
						state.admin = action.payload.admin;
						state.token = action.payload.token;
						state.expiration = action.payload.expiration;
						state.twoFactorRequired = false;
						state.isLogged = !!action.payload.token;
						state.notifications = action.payload.notifications;
					}
				},
			)

			.addCase(loginStaff.rejected, (state, action: PayloadAction<any>) => {
				state.loading = false;
				state.error = action.payload;
			})

			// Verify 2FA
			.addCase(verifyTwoFactor.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				verifyTwoFactor.fulfilled,
				(
					state,
					action: PayloadAction<
						| {
								admin: Admin;
								token: string;
								expiration: string;
								notifications?: any[] | null;
						  }
						| any
					>,
				) => {
					state.loading = false;
					state.admin = action.payload.admin;
					state.token = action.payload.token;
					state.expiration = action.payload.expiration;
					state.twoFactorRequired = false;
					state.isLogged = true;
					state.notifications = action.payload.notifications;
				},
			)
			.addCase(verifyTwoFactor.rejected, (state, action: PayloadAction<any>) => {
				state.loading = false;
				state.error = action.payload;
			})

			.addCase(fetchStaff.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchStaff.fulfilled, (state, action: PayloadAction<Admin>) => {
				state.loading = false;
				state.admin = action.payload;
			})
			.addCase(fetchStaff.rejected, (state, action: PayloadAction<any>) => {
				state.loading = false;
				state.error = action.payload;
			});
	},
});

export const { logout, setLogoutTimer, setInactivityTimer, resetInactivityTimer } =
	authSlice.actions;
export default authSlice.reducer;
